import { Modal as AntModal, ModalProps as AntModalProps } from 'antd'
import { ReactNode } from 'react'

export interface ModalProps extends AntModalProps {
  /**
   * Modal title
   */
  title: string

  /**
   * Whether the modal is visible
   */
  open: boolean

  /**
   * Close handler
   */
  onClose: () => void

  /**
   * OK button handler (optional)
   */
  onOk?: () => void

  /**
   * OK button text
   * @default 'OK'
   */
  okText?: string

  /**
   * Cancel button text
   * @default 'Cancel'
   */
  cancelText?: string

  /**
   * Modal content
   */
  children: ReactNode

  /**
   * Loading state (disables buttons)
   */
  confirmLoading?: boolean

  /**
   * Modal width
   * @default 520
   */
  width?: number | string

  /**
   * Whether to show close icon
   * @default true
   */
  closable?: boolean

  /**
   * Centered modal
   */
  centered?: boolean

  /**
   * Hide footer
   */
  footer?: ReactNode | null
}

/**
 * Modal Component
 *
 * A dialog component that overlays the page content.
 *
 * @example
 * <Modal
 *   title="Confirm Delete"
 *   open={isOpen}
 *   onClose={handleClose}
 *   onOk={handleDelete}
 *   okText="Delete"
 *   cancelText="Cancel"
 * >
 *   <p>Are you sure you want to delete this item?</p>
 * </Modal>
 *
 * @example
 * // Modal without footer
 * <Modal
 *   title="Information"
 *   open={isOpen}
 *   onClose={handleClose}
 *   footer={null}
 * >
 *   <p>This is an informational modal.</p>
 * </Modal>
 */
export function Modal({
  title,
  open,
  onClose,
  onOk,
  okText = 'OK',
  cancelText = 'Cancel',
  children,
  confirmLoading,
  centered = false,
  ...props
}: ModalProps) {
  return (
    <AntModal
      title={title}
      open={open}
      onCancel={onClose}
      onOk={onOk}
      okText={okText}
      cancelText={cancelText}
      confirmLoading={confirmLoading}
      centered={centered}
      {...props}
    >
      {children}
    </AntModal>
  )
}

/**
 * ConfirmModal Component
 *
 * A specialized modal for confirmation dialogs with danger styling.
 * Follows the AVA design system confirm dialog pattern.
 *
 * @example
 * <ConfirmModal
 *   title="Delete Item"
 *   open={isOpen}
 *   onClose={handleClose}
 *   onConfirm={handleDelete}
 *   confirmText="Delete"
 *   danger
 * >
 *   Are you sure you want to delete this item? This action cannot be undone.
 * </ConfirmModal>
 */
export interface ConfirmModalProps {
  title: string
  open: boolean
  onClose: () => void
  onConfirm: () => void
  confirmText?: string
  cancelText?: string
  children: ReactNode
  loading?: boolean
  danger?: boolean
  /**
   * z-index of the modal. Defaults to 1100 to appear above drawers (which are 1000).
   * @default 1100
   */
  zIndex?: number
}

export function ConfirmModal({
  title,
  open,
  onClose,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'No',
  children,
  loading = false,
  danger = false,
  zIndex = 1100,
}: ConfirmModalProps) {
  return (
    <AntModal
      open={open}
      onCancel={onClose}
      onOk={onConfirm}
      okText={confirmText}
      cancelText={cancelText}
      confirmLoading={loading}
      okButtonProps={{ danger, size: 'small' }}
      cancelButtonProps={{ size: 'small' }}
      footer={null}
      closable={false}
      centered
      width={416}
      zIndex={zIndex}
      styles={{
        content: {
          padding: '32px 32px 24px 32px',
          borderRadius: 2,
        },
        body: {
          padding: 0,
        },
      }}
    >
      <div className="flex flex-col gap-6">
        {/* Content area with icon and text */}
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div
            className="w-[22px] h-[22px] flex-shrink-0 rounded-full flex items-center justify-center"
            style={{ backgroundColor: danger ? '#891D44' : '#faad14' }}
          >
            <span className="text-white text-xs font-bold">!</span>
          </div>

          {/* Text content */}
          <div className="flex-1 flex flex-col gap-2">
            <div
              className="text-[16px] font-medium leading-6"
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
            >
              {title}
            </div>
            <div
              className="text-[14px] font-normal leading-[22px]"
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
            >
              {children}
            </div>
          </div>
        </div>

        {/* Button area */}
        <div className="flex justify-end items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-[15px] py-1 bg-white border border-[#D9D9D9] rounded-sm text-[14px] leading-[22px] hover:border-[#40a9ff] hover:text-[#40a9ff] transition-colors disabled:opacity-50"
            style={{ color: 'rgba(0, 0, 0, 0.85)' }}
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`px-[15px] py-1 rounded-sm text-[14px] leading-[22px] text-white transition-colors disabled:opacity-50 ${
              danger
                ? 'bg-[#891D44] hover:bg-[#a02350] border border-[#891D44]'
                : 'bg-[#132B51] hover:bg-[#1a3a6b] border border-[#132B51]'
            }`}
          >
            {loading ? 'Loading...' : confirmText}
          </button>
        </div>
      </div>
    </AntModal>
  )
}
